import React, { useEffect, useState } from "react";
import { Bell, X } from "lucide-react";
import { toast } from "sonner";
import {
  isPushSupported,
  currentPermission,
  getExistingSubscription,
  enablePushNotifications,
  reconnectPushNotifications,
  sendTestPush,
} from "../lib/push";

const DISMISS_KEY = "shelflife_push_prompt_dismissed_until";
// If the user dismisses the soft prompt without enabling, leave them alone
// for two weeks so the app doesn't nag.
const DISMISS_WINDOW_MS = 14 * 24 * 60 * 60 * 1000;

function isDismissed() {
  try {
    const until = Number(localStorage.getItem(DISMISS_KEY) || 0);
    return Number.isFinite(until) && until > Date.now();
  } catch {
    return false;
  }
}

function markDismissed() {
  try {
    localStorage.setItem(DISMISS_KEY, String(Date.now() + DISMISS_WINDOW_MS));
  } catch {
    /* localStorage may be disabled in private mode — fine */
  }
}

/**
 * Post-login soft prompt asking the user to enable push notifications.
 *
 * Renders nothing when:
 *   - Push isn't supported on this browser
 *   - The user has already granted permission AND is subscribed
 *   - The user has previously dismissed (within the dismiss window)
 *
 * Renders the friendly card when:
 *   - permission === "default" (never asked) → primary "Enable" CTA
 *   - permission === "granted" but no subscription → re-subscribe CTA
 *   - permission === "denied" → instructional message (no CTA, just dismiss)
 */
export default function PushPermissionPrompt() {
  const supported = isPushSupported();
  const [perm, setPerm] = useState(supported ? currentPermission() : "unsupported");
  const [subscribed, setSubscribed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [dismissed, setDismissed] = useState(isDismissed());
  // Don't flash the card on first paint — wait for a single re-render so the
  // app shell loads first and we don't compete with login transitions.
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setReady(true), 600);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    if (!supported) return undefined;
    let mounted = true;
    getExistingSubscription()
      .then((s) => { if (mounted) setSubscribed(!!s); })
      .catch(() => { if (mounted) setSubscribed(false); });
    return () => { mounted = false; };
  }, [supported]);

  if (!supported) return null;
  if (!ready) return null;
  if (dismissed) return null;
  // Happy path — already enabled and subscribed: nothing to nag about.
  if (perm === "granted" && subscribed) return null;

  // Already granted but the device has no subscription → recovery mode.
  // Common on Samsung after FCM token resets or aggressive battery savers.
  const isReconnect = perm === "granted" && !subscribed;

  const onEnable = async () => {
    setBusy(true);
    try {
      if (isReconnect) {
        await reconnectPushNotifications();
      } else {
        await enablePushNotifications();
      }
      setSubscribed(true);
      setPerm(currentPermission());
      try {
        const r = await sendTestPush();
        if (r.sent > 0) {
          toast.success("Test notification sent — check your lock screen");
        } else {
          toast.success("Notifications enabled — alerts will arrive when active");
        }
      } catch {
        toast.success("Notifications enabled");
      }
    } catch (e) {
      setPerm(currentPermission());
      const msg = e?.message || "Could not enable notifications";
      // Friendly Android-specific guidance.
      if (currentPermission() === "denied") {
        toast.error("Notifications are blocked. Enable them in Android app settings.");
      } else {
        toast.error(msg);
      }
    } finally {
      setBusy(false);
    }
  };

  const onDismiss = () => {
    markDismissed();
    setDismissed(true);
  };

  /* ---- denied state: show instructions, no CTA, allow dismiss ---- */
  if (perm === "denied") {
    return (
      <div
        className="mx-4 mt-3 p-3 bg-status-urgent-bg rounded-card shadow-rest flex items-start gap-3"
        role="status"
        data-testid="push-prompt-denied"
      >
        <Bell size={18} strokeWidth={2.5} className="text-status-urgent shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <div className="font-display font-bold text-sm text-status-urgent">
            Notifications are blocked
          </div>
          <div className="text-[12px] text-ink/80 mt-1 leading-snug">
            Enable them in <span className="font-semibold">Android Settings → Apps → ShelfLife → Notifications</span> so expiration alerts can reach your phone.
          </div>
        </div>
        <button
          type="button"
          onClick={onDismiss}
          className="shrink-0 -mt-1 -mr-1 p-1 text-status-urgent/70 hover:text-status-urgent"
          aria-label="Dismiss"
          data-testid="push-prompt-dismiss"
        >
          <X size={16} />
        </button>
      </div>
    );
  }

  /* ---- default/granted-but-unsubscribed: positive CTA ---- */
  return (
    <div
      className="mx-4 mt-3 p-3 bg-paper-sunk rounded-card shadow-rest flex items-start gap-3"
      role="status"
      data-testid={isReconnect ? "push-prompt-reconnect" : "push-prompt-enable"}
    >
      <Bell size={18} strokeWidth={2.5} className="text-brand shrink-0 mt-0.5" />
      <div className="flex-1 min-w-0">
        <div className="font-display font-bold text-sm">
          {isReconnect ? "Reconnect notifications" : "Turn on expiration alerts"}
        </div>
        <div className="text-[12px] text-ink/80 mt-1 leading-snug">
          {isReconnect
            ? "Your device lost its push subscription. Tap below to re-register so expiration alerts reach your phone again."
            : "Get reminded on your phone when items hit the 7-day, 3-day and expired thresholds — even when ShelfLife is closed."}
        </div>
        <div className="flex items-center gap-2 mt-2">
          <button
            type="button"
            onClick={onEnable}
            disabled={busy}
            className="btn-primary h-9 px-3 text-[12px]"
            data-testid={isReconnect ? "push-prompt-reconnect-btn" : "push-prompt-enable-btn"}
          >
            {busy
              ? (isReconnect ? "Reconnecting…" : "Enabling…")
              : (isReconnect ? "Reconnect notifications" : "Enable notifications")}
          </button>
          <button
            type="button"
            onClick={onDismiss}
            className="text-[11px] font-mono uppercase tracking-wider text-ink/60 hover:text-ink"
            data-testid="push-prompt-not-now"
          >
            Not now
          </button>
        </div>
      </div>
    </div>
  );
}
